import { useState, useEffect } from 'react'
import {
  Box,
  Flex,
  Input,
  Text,
  Button,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useCreateMeterReading } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { MeterReadingInsert } from '../types'

interface MeterReadingModalProps {
  isOpen: boolean
  onClose: () => void
  distributionKeyId?: string
  fiscalYearId?: string
  residenceId: string
}

export function MeterReadingModal({
  isOpen,
  onClose,
  distributionKeyId,
  fiscalYearId,
}: MeterReadingModalProps) {
  const createMutation = useCreateMeterReading()

  // Fetch units included in this distribution key
  const { data: includedUnits } = useQuery({
    queryKey: ['distribution_key_units', distributionKeyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('distribution_key_units')
        .select('unit_id, is_included, units (id, unit_number, stairwell, floor)')
        .eq('distribution_key_id', distributionKeyId!)
        .eq('is_included', true)
        .order('units(unit_number)')

      if (error) throw error
      return data.map(d => d.units).filter(Boolean)
    },
    enabled: !!distributionKeyId && isOpen,
  })

  const [formData, setFormData] = useState<Partial<MeterReadingInsert>>({
    distribution_key_id: distributionKeyId,
    fiscal_year_id: fiscalYearId,
    unit_id: '',
    index_start: undefined,
    index_end: undefined,
    consumption: undefined,
    reading_date: new Date().toISOString().split('T')[0],
    is_estimated: false,
    notes: '',
    period_start: '',
    period_end: '',
  })

  // Reset form when modal opens with new key
  useEffect(() => {
    if (isOpen) {
      setFormData({
        distribution_key_id: distributionKeyId,
        fiscal_year_id: fiscalYearId,
        unit_id: '',
        index_start: undefined,
        index_end: undefined,
        consumption: undefined,
        reading_date: new Date().toISOString().split('T')[0],
        is_estimated: false,
        notes: '',
        period_start: '',
        period_end: '',
      })
    }
  }, [isOpen, distributionKeyId, fiscalYearId])

  // Auto-calculate consumption when indexes change
  useEffect(() => {
    if (formData.index_start != null && formData.index_end != null) {
      const start = Number(formData.index_start)
      const end = Number(formData.index_end)
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        setFormData(prev => ({ ...prev, consumption: end - start }))
      }
    }
  }, [formData.index_start, formData.index_end])

  const handleClose = () => {
    setFormData({
      distribution_key_id: distributionKeyId,
      fiscal_year_id: fiscalYearId,
      unit_id: '',
      index_start: undefined,
      index_end: undefined,
      consumption: undefined,
      reading_date: new Date().toISOString().split('T')[0],
      is_estimated: false,
      notes: '',
      period_start: '',
      period_end: '',
    })
    onClose()
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.unit_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez sélectionner un lot',
        type: 'error',
      })
      return
    }

    if (!formData.index_end) {
      toaster.create({
        title: 'Erreur',
        description: "L'index de fin est obligatoire",
        type: 'error',
      })
      return
    }

    if (!formData.consumption) {
      toaster.create({
        title: 'Erreur',
        description: 'La consommation est obligatoire',
        type: 'error',
      })
      return
    }

    if (!formData.reading_date) {
      toaster.create({
        title: 'Erreur',
        description: 'La date de relevé est obligatoire',
        type: 'error',
      })
      return
    }

    if (!formData.period_start || !formData.period_end) {
      toaster.create({
        title: 'Erreur',
        description: 'Les dates de période sont obligatoires',
        type: 'error',
      })
      return
    }

    try {
      await createMutation.mutateAsync(formData as Omit<MeterReadingInsert, 'id' | 'created_at' | 'updated_at'>)

      toaster.create({
        title: 'Succès',
        description: 'Relevé enregistré',
        type: 'success',
      })

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'enregistrement",
        type: 'error',
      })
    }
  }

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="lg"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="600px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Ajouter un relevé de compteur</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Flex direction="column" gap={4}>
              {/* Unit selection */}
              <Field label="Lot" required>
                <NativeSelectRoot size="md">
                  <NativeSelectField
                    value={formData.unit_id || ''}
                    onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
                  >
                    <option value="">Sélectionner un lot...</option>
                    {includedUnits?.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        Lot {unit.unit_number}
                        {unit.stairwell && ` - Escalier ${unit.stairwell}`}
                        {unit.floor != null && ` - Étage ${unit.floor}`}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              {/* Period dates */}
              <Flex gap={4}>
                <Box flex={1}>
                  <Field label="Début de période" required>
                    <Input
                      type="date"
                      size="md"
                      value={formData.period_start || ''}
                      onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                    />
                  </Field>
                </Box>
                <Box flex={1}>
                  <Field label="Fin de période" required>
                    <Input
                      type="date"
                      size="md"
                      value={formData.period_end || ''}
                      onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                    />
                  </Field>
                </Box>
              </Flex>

              {/* Indexes */}
              <Flex gap={4}>
                <Box flex={1}>
                  <Field
                    label="Index début"
                    helperText="Laissez vide si inconnu"
                  >
                    <Input
                      type="number"
                      size="md"
                      min={0}
                      step="0.001"
                      value={formData.index_start ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        index_start: e.target.value ? Number(e.target.value) : undefined
                      })}
                    />
                  </Field>
                </Box>
                <Box flex={1}>
                  <Field label="Index fin" required>
                    <Input
                      type="number"
                      size="md"
                      min={0}
                      step="0.001"
                      value={formData.index_end ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        index_end: e.target.value ? Number(e.target.value) : undefined
                      })}
                    />
                  </Field>
                </Box>
              </Flex>

              {/* Consumption */}
              <Field
                label="Consommation"
                required
                helperText={
                  formData.index_start != null && formData.index_end != null
                    ? 'Calculée automatiquement'
                    : 'Saisissez manuellement si index début inconnu'
                }
              >
                <Input
                  type="number"
                  size="md"
                  min={0}
                  step="0.001"
                  value={formData.consumption ?? ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    consumption: e.target.value ? Number(e.target.value) : undefined
                  })}
                  disabled={formData.index_start != null && formData.index_end != null}
                />
              </Field>

              {/* Reading date */}
              <Field label="Date de relevé" required>
                <Input
                  type="date"
                  size="md"
                  value={formData.reading_date || ''}
                  onChange={(e) => setFormData({ ...formData, reading_date: e.target.value })}
                />
              </Field>

              {/* Estimated checkbox */}
              <Box
                p={3}
                bg={formData.is_estimated ? 'orange.50' : 'gray.50'}
                borderRadius="md"
                borderWidth="1px"
                borderColor={formData.is_estimated ? 'orange.200' : 'gray.200'}
              >
                <Flex align="center" gap={2}>
                  <input
                    type="checkbox"
                    checked={formData.is_estimated || false}
                    onChange={(e) => setFormData({ ...formData, is_estimated: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    Relevé estimé (compteur inaccessible)
                  </Text>
                </Flex>
              </Box>

              {/* Notes */}
              <Field label="Notes">
                <Input
                  size="md"
                  placeholder="Observations éventuelles..."
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Field>
            </Flex>
          </DialogBody>

          <DialogFooter>
            <Flex gap={2} justify="flex-end">
              <Button variant="ghost" onClick={handleClose}>
                Annuler
              </Button>
              <Button
                colorPalette="brand"
                onClick={handleSubmit}
                loading={createMutation.isPending}
              >
                Enregistrer
              </Button>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
